import SectionCard from '@/components/base/SectionCard';
import PlatformIcon from '@/components/base/PlatformIcon';
import PlatformAudienceSplit from '@/components/base/PlatformAudienceSplit';
import MarketAudienceSplit from '@/components/base/MarketAudienceSplit';
import type { CreatorProfile } from '@/types/portal';
import { formatCompact } from '@/utils/format';

export default function PlatformPanel({ creator }: { creator: CreatorProfile }) {
  const combinedFollowers = creator.platforms.reduce((sum, account) => sum + account.followers, 0);

  return (
    <SectionCard title="Connected platforms" subtitle="Verified audience data pulled from each account">
      {creator.platforms.length > 0 ? (
        <div className="mb-4 rounded-lg border border-background-200 bg-background-100/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              <i className="ri-bar-chart-grouped-line" />
              Audience split
            </span>
            <span className="text-xs text-foreground-500">
              {formatCompact(combinedFollowers)} combined followers
            </span>
          </div>
          <PlatformAudienceSplit
            platforms={creator.platforms.map((account) => ({
              platform: account.platform,
              followers: account.followers,
            }))}
          />
        </div>
      ) : null}
      {creator.marketAudience && creator.marketAudience.length > 1 ? (
        <div className="mb-4 rounded-lg border border-background-200 bg-background-100/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              <i className="ri-map-pin-2-line" />
              Followers by market
            </span>
            <span className="text-xs text-foreground-500">
              Across {creator.marketAudience.length} markets
            </span>
          </div>
          <MarketAudienceSplit markets={creator.marketAudience} />
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {creator.platforms.map((account) => (
          <div
            key={`${account.platform}-${account.handle}`}
            className="rounded-lg border border-background-200 bg-background-50 p-4"
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200 bg-background-100 text-base">
                  <PlatformIcon platform={account.platform} className="text-base" />
                </span>
                <span className="text-sm font-medium text-foreground-900">
                  {account.platform}
                </span>
              </span>
              {account.verified ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-label text-accent-700">
                  <i className="ri-verified-badge-fill text-[13px]" />
                  Verified
                </span>
              ) : (
                <span className="text-[11px] font-label text-foreground-400">Unverified</span>
              )}
            </div>
            <p className="mt-2 text-xs text-foreground-500">{account.handle}</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div>
                <p className="text-[11px] text-foreground-400">Followers</p>
                <p className="font-heading text-sm font-semibold text-foreground-900">
                  {formatCompact(account.followers)}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-foreground-400">Eng. rate</p>
                <p className="font-heading text-sm font-semibold text-foreground-900">
                  {account.engagementRate}%
                </p>
              </div>
              <div>
                <p className="text-[11px] text-foreground-400">Avg views</p>
                <p className="font-heading text-sm font-semibold text-foreground-900">
                  {formatCompact(account.avgViews)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}